/**
 * ArtifactPanel Component
 *
 * Painel lateral resizável para exibição de artefatos
 * - Resize com persistência no localStorage
 * - Animações de slide-in/out
 * - Suporte a fullscreen
 * - Keyboard shortcuts (Esc)
 */

import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { ArtifactHeader } from './ArtifactHeader';
import { ArtifactRenderer } from './ArtifactRenderer';
import { ArtifactLoading } from './ArtifactLoading';
import {
  PANEL_CONFIG,
  ANIMATION_DURATIONS,
  getInitialPanelWidth,
  savePanelWidth,
} from '../constants';
import type { Artifact } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface ArtifactPanelProps {
  artifact: Artifact | null;
  onClose?: () => void;
  isLoading?: boolean;
  isUpdating?: boolean;
  loadingInfo?: {
    type?: string;
    title?: string;
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ArtifactPanel: React.FC<ArtifactPanelProps> = memo(
  ({ artifact, onClose, isLoading = false, isUpdating = false, loadingInfo }) => {
    // State
    const [isVisible, setIsVisible] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [panelWidth, setPanelWidth] = useState<number>(getInitialPanelWidth);
    const [isResizing, setIsResizing] = useState(false);

    // Refs
    const panelRef = useRef<HTMLDivElement>(null);
    const startWidthRef = useRef<number>(0);
    const startXRef = useRef<number>(0);

    // ==========================================================================
    // EFFECTS
    // ==========================================================================

    // Slide-in animation on mount
    useEffect(() => {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (isFullscreen) {
            setIsFullscreen(false);
          } else {
            handleClose();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    // Resize event listeners
    useEffect(() => {
      if (!isResizing) return;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = startXRef.current - e.clientX;
        const newWidth = Math.max(
          PANEL_CONFIG.MIN_WIDTH,
          Math.min(window.innerWidth * PANEL_CONFIG.MAX_WIDTH_RATIO, startWidthRef.current + deltaX)
        );
        setPanelWidth(newWidth);
        savePanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      // Also handle mouse leaving the window
      const handleMouseLeave = () => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseLeave);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseLeave);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }, [isResizing]);

    // ==========================================================================
    // HANDLERS
    // ==========================================================================

    const handleClose = useCallback(() => {
      setIsVisible(false);
      setTimeout(() => {
        onClose?.();
      }, ANIMATION_DURATIONS.SLIDE_IN);
    }, [onClose]);

    const handleResizeStart = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      if (panelRef.current) {
        startWidthRef.current = panelRef.current.offsetWidth;
      }
    }, []);

    const toggleFullscreen = useCallback(() => {
      setIsFullscreen((prev) => !prev);
    }, []);

    // ==========================================================================
    // RENDER
    // ==========================================================================

    const showLoading = isLoading && !artifact;

    return (
      <>
        {/* Side Panel */}
        <div
          ref={panelRef}
          className={`
            fixed top-0 right-0 h-full z-50
            bg-[#0f0f0f] border-l border-white/10
            flex flex-col
            transition-transform duration-300 ease-out
            ${isVisible ? 'translate-x-0' : 'translate-x-full'}
            ${isResizing ? 'select-none' : ''}
            ${isUpdating ? 'opacity-70' : ''}
          `}
          style={{ width: `${panelWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            className={`
              absolute left-0 top-0 bottom-0 w-2
              cursor-col-resize
              ${isResizing ? 'bg-yellow-500/50' : 'bg-transparent'}
            `}
            onMouseDown={handleResizeStart}
          />

          {/* Content */}
          <div className="flex flex-col h-full overflow-hidden">
            {showLoading ? (
              <ArtifactLoading type={loadingInfo?.type} title={loadingInfo?.title} />
            ) : artifact ? (
              <>
                <ArtifactHeader
                  artifact={artifact}
                  onClose={handleClose}
                  onFullscreen={toggleFullscreen}
                  updatedAt={artifact.updatedAt}
                />
                <div className="flex-1 overflow-auto">
                  <ArtifactRenderer artifact={artifact} />
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Fullscreen Modal */}
        {isFullscreen && artifact && (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
            onClick={toggleFullscreen}
          >
            <div
              className="w-[95vw] h-[95vh] bg-[#0f0f0f] rounded-lg border border-white/10 flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <ArtifactHeader
                artifact={artifact}
                onClose={toggleFullscreen}
                isFullscreen={true}
                updatedAt={artifact.updatedAt}
              />
              <div className="flex-1 overflow-auto">
                <ArtifactRenderer artifact={artifact} />
              </div>
            </div>
          </div>
        )}

        {/* Backdrop for mobile */}
        {isVisible && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={handleClose}
          />
        )}
      </>
    );
  }
);

ArtifactPanel.displayName = 'ArtifactPanel';

export default ArtifactPanel;
